/**
 * Google Places autocomplete for the USCIS payment authorization billing
 * address — Alpine component wrapped around the payer fields of the plain
 * POST form in livewire/partials/uscis-payment-authorization.blade.php.
 *
 * Same contract as enhanced-address-autocomplete: manual typing is the
 * baseline and always works; Google is a progressive enhancement layered on
 * when the central loader resolves, and a loader failure is silent. The form
 * is a plain POST (no Livewire model), so a selection writes straight into
 * the sibling inputs by id — the ids the blade already renders.
 *
 * fill() never blanks a field: Google frequently omits a component (zip on
 * some international addresses, street_number outside the US), and erasing
 * what the client already typed because Google didn't know it would be the
 * address-history bug all over again.
 */
import loadGoogleMaps from '../lib/google-maps-loader';
import alvvaParsePlace from '../lib/google-place-parser';

export default function payBillingAutocomplete() {
    return {
        autocomplete: null,

        init() {
            loadGoogleMaps()
                .then(() => this.attach())
                .catch(() => {}); // silent fallback — manual entry already works
        },

        attach() {
            const line1 = document.getElementById('pay-cardholder_address_line1');
            if (!line1 || this.autocomplete || !window.google?.maps?.places) return;

            this.autocomplete = new window.google.maps.places.Autocomplete(line1, {
                types: ['address'],
                fields: ['address_components', 'formatted_address'],
            });

            // Enter inside the Google dropdown selects a suggestion; without
            // this it ALSO submits the form around it, posting a half-filled
            // billing address the moment the client picks their street.
            line1.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' && document.querySelector('.pac-container:not([style*="display: none"])')) {
                    e.preventDefault();
                }
            });

            this.autocomplete.addListener('place_changed', () => {
                const place = this.autocomplete.getPlace();
                if (!place || !place.address_components) return;

                const parsed = alvvaParsePlace(place);

                this.fill('pay-cardholder_address_line1', parsed.street);
                this.fill('pay-cardholder_city', parsed.city);
                this.fill('pay-cardholder_state', parsed.state);
                this.fill('pay-cardholder_postal_code', parsed.zip);
                this.fill('pay-cardholder_country', parsed.country);
            });
        },

        fill(id, value) {
            if (!value) return;
            const el = document.getElementById(id);
            if (el) el.value = value;
        },
    };
}
